import {createContext, use, useState, useMemo, useEffect, useEffectEvent, useCallback} from 'react'
import type React from 'react'
import {fetchIndex} from '../../../utils/rag-index-manager'
import type {RAGContextType, Index} from '../../../types'

const RAGContext = createContext<RAGContextType>({} as RAGContextType)

type RAGContextProviderProps = {
  children: React.ReactNode
  initialIndex?: Index
  skipInitialIndexFetch?: boolean
}

export const RAGContextProvider = ({children, initialIndex, skipInitialIndexFetch}: RAGContextProviderProps) => {
  const [index, setIndex] = useState<RAGContextType['index']>(initialIndex ?? null)
  const [isFetchingIndex, setIsFetchingIndex] = useState(false)

  const pollUntilCompleted = useCallback(async () => {
    setIsFetchingIndex(true)
    let fetchedIndex = await fetchIndex()
    setIndex(fetchedIndex)

    while (fetchedIndex?.status === 'InProgress' || fetchedIndex?.status === null) {
      // pause for 1000 ms
      await new Promise(resolve => setTimeout(resolve, 1000))
      fetchedIndex = await fetchIndex()
      setIndex(fetchedIndex)
    }

    setIsFetchingIndex(false)
  }, [])

  const syncInitialIndex = useEffectEvent(() => {
    if (initialIndex || skipInitialIndexFetch) return
    void pollUntilCompleted()
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    syncInitialIndex()
  }, [])

  const contextValue = useMemo(
    () => ({index, setIndex, isFetchingIndex, pollUntilCompleted}),
    [index, isFetchingIndex, pollUntilCompleted],
  )

  return <RAGContext value={contextValue}>{children}</RAGContext>
}

export const useRAGContext = () => {
  const context = use(RAGContext)
  if (context === undefined) {
    throw new Error('useRAGContext must be used within a RAGContextProvider')
  }

  return context
}
